/**
 * Selector de un stakeholder de la misma entidad.
 *
 * Lo usa «marcar como duplicado de…» (§7.3) y la elección de destino en una
 * fusión. Busca contra el servidor porque una entidad puede tener cientos de
 * actores y filtrar en el cliente obligaría a traerlos todos.
 */

import { Search } from 'lucide-react'
import { useState } from 'react'

import { useTabla } from '../../api/hooks'
import type { StakeholderFila } from '../../api/tipos'
import { Cargando, Entrada, EtiquetaTipo, Vacio } from '../../ui/primitivos'
import { cx } from '../../ui/util'

export function SelectorStakeholder({
  entidadId,
  excluir,
  seleccionado,
  onSeleccionar,
}: {
  entidadId: number
  /** Ids que no tiene sentido ofrecer, típicamente el propio registro. */
  excluir: number[]
  seleccionado: number | null
  onSeleccionar: (fila: StakeholderFila) => void
}) {
  const [texto, setTexto] = useState('')
  const consulta = useTabla(entidadId, { q: texto || undefined, page: 1 })

  const filas = (consulta.data?.results ?? []).filter((f) => !excluir.includes(f.id))

  return (
    <div className="grid gap-2">
      <div className="relative">
        <Search
          size={14}
          className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-[var(--color-texto-tenue)]"
        />
        <Entrada
          autoFocus
          className="pl-8"
          placeholder="Buscar por nombre, sigla o categoría…"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
      </div>

      <div className="scroll-sutil max-h-64 overflow-y-auto rounded-md border border-[var(--color-borde)]">
        {consulta.isLoading ? (
          <Cargando texto="Buscando…" />
        ) : !filas.length ? (
          <Vacio
            titulo="Ningún stakeholder coincide"
            detalle={
              texto ? 'Prueba con otro término.' : 'No hay otros actores en esta entidad.'
            }
          />
        ) : (
          <ul className="divide-y divide-[var(--color-borde)]">
            {filas.map((fila) => (
              <li key={fila.id}>
                <button
                  onClick={() => onSeleccionar(fila)}
                  className={cx(
                    'flex w-full items-center gap-2 px-2.5 py-2 text-left',
                    seleccionado === fila.id
                      ? 'bg-[var(--color-acento-suave)]'
                      : 'hover:bg-[var(--color-acento-suave)]',
                  )}
                >
                  <input
                    type="radio"
                    checked={seleccionado === fila.id}
                    readOnly
                    className="shrink-0 accent-[var(--color-acento)]"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium">
                      {fila.nombre}
                      {fila.sigla && (
                        <span className="ml-1 text-[11px] text-[var(--color-texto-tenue)]">
                          ({fila.sigla})
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 flex items-center gap-2 text-[11px] text-[var(--color-texto-tenue)]">
                      <EtiquetaTipo tipo={fila.tipo} />
                      <span className="truncate">{fila.categoria || 'Sin categoría'}</span>
                    </span>
                  </span>
                  <span className="shrink-0 text-[11px] tabular text-[var(--color-texto-tenue)]">
                    {fila.n_fuentes} fuentes
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
